import { useEffect, useState, useCallback } from "react";
import { api, EGG_ORDER, BREED_LABELS, fmt, fmtDec } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, StatCard } from "@/components/shared";
import { Egg, Percent, Wheat, Skull, Wallet, CalendarClock, AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";

const COLORS = ["#16a34a", "#f59e0b", "#0ea5e9", "#a855f7", "#ef4444", "#14b8a6", "#eab308", "#ec4899"];
const BREED_TONE = {
  kirmizi: "bg-red-500/10 text-red-600 dark:text-red-400",
  tintin: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  beyaz: "bg-slate-400/15 text-slate-600 dark:text-slate-300",
};

function OperatorHome() {
  const { myCoops, user } = useApp();
  const subs = myCoops.filter((c) => c.type === "coop");
  const [live, setLive] = useState({});
  const [latest, setLatest] = useState({});
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    (async () => {
      const [lv, prod] = await Promise.all([api.get("/coops/live"), api.get("/production")]);
      setLive(Object.fromEntries(lv.data.map((x) => [x.coop_id, x])));
      const byCoop = {};
      prod.data.forEach((p) => { if (!byCoop[p.coop_id] || p.date > byCoop[p.coop_id].date) byCoop[p.coop_id] = p; });
      setLatest(byCoop);
    })();
  }, []);

  return (
    <div>
      <PageHeader title={`Hoş geldiniz${user?.name ? ", " + user.name : ""}`} subtitle="Sorumlu olduğunuz kümeslerin güncel özeti" />
      {subs.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="operator-empty">Henüz size atanmış bir kümes yok. Lütfen yöneticinizle iletişime geçin.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="operator-cards">
          {subs.map((c) => {
            const lv = live[c.id];
            const last = latest[c.id];
            const isToday = last?.date === today;
            return (
              <div key={c.id} className="rounded-xl border border-border bg-card p-5" data-testid={`operator-card-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-head font-semibold">{c.name}</h3>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${BREED_TONE[c.breed] || "bg-secondary"}`}>{BREED_LABELS[c.breed] || "-"}</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Egg size={20} /></div>
                  <div>
                    <div className="font-head text-2xl font-bold tabular-nums" data-testid={`operator-live-${c.id}`}>{fmt(lv?.current_count ?? 0)}</div>
                    <div className="text-[11px] text-muted-foreground">canlı tavuk {lv?.last_date ? `· ${lv.last_date}` : ""}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="rounded-lg bg-secondary/50 px-3 py-2">
                    <div className="text-[11px] text-muted-foreground">Son Üretim</div>
                    <div className="font-semibold tabular-nums">{fmt(last?.total_production || 0)} adet</div>
                  </div>
                  <div className="rounded-lg bg-secondary/50 px-3 py-2">
                    <div className="text-[11px] text-muted-foreground">Randıman</div>
                    <div className="font-semibold tabular-nums text-primary">%{fmtDec(last?.yield_pct || 0)}</div>
                  </div>
                </div>
                {!isToday && <div className="mt-3 text-[11px] text-amber-600 font-medium" data-testid={`operator-warn-${c.id}`}>Bugün için üretim girilmedi</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { isOperator } = useApp();
  if (isOperator) return <OperatorHome />;
  return <AdminDashboard />;
}

function AdminDashboard() {
  const { meta, selectedCoop, user } = useApp();
  const canFin = user?.role === "admin" || !!user?.permissions?.view_financials;
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [costTrends, setCostTrends] = useState([]);
  const [recv, setRecv] = useState(null);

  const load = useCallback(async () => {
    const cp = selectedCoop !== "all" ? `&coop_id=${encodeURIComponent(selectedCoop)}` : "";
    const [s, t, ct, r] = await Promise.all([
      api.get(`/dashboard/summary?day=${day}${cp}`),
      api.get(`/dashboard/trends?days=30${cp}`),
      api.get(`/dashboard/cost-trends?months=6`),
      canFin ? api.get(`/dashboard/receivables`) : Promise.resolve({ data: null }),
    ]);
    setSummary(s.data);
    setTrends(t.data);
    setCostTrends(ct.data);
    setRecv(r.data);
  }, [day, selectedCoop, canFin]);

  useEffect(() => { load(); }, [load]);

  const sizeLabel = (k) => meta.egg_sizes.find((e) => e.key === k)?.label || k;
  const tl = (v) => (Number(v) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " TL";

  return (
    <div>
      <PageHeader
        title="Kontrol Paneli"
        subtitle="Kümesler arası günlük performans ve trend analizi"
        action={
          <input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            data-testid="dashboard-date"
            className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none"
          />
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Egg} label="Toplam Yumurta" value={fmt(summary?.total_eggs)} sub={`${summary?.active_coops || 0} kümes kaydı`} tone="primary" testid="stat-eggs" />
        <StatCard icon={Percent} label="Ort. Randıman" value={`%${fmtDec(summary?.avg_yield)}`} sub={`${fmt(summary?.total_chickens)} tavuk`} tone="sky" testid="stat-yield" />
        <StatCard icon={Wheat} label="Yem Tüketimi" value={`${fmtDec(summary?.total_feed_kg)} kg`} sub="Günlük toplam" tone="amber" testid="stat-feed" />
        <StatCard icon={Skull} label="Ölüm / Telef" value={fmt(summary?.total_deaths)} sub="Günlük toplam adet" tone="red" testid="stat-deaths" />
      </div>

      {canFin && <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="receivables-cards">
        <StatCard icon={Wallet} label="Toplam Alacak" value={tl(recv?.total_receivable)} sub="Tüm cari bakiyeler" tone="primary" testid="recv-total" />
        <StatCard icon={CalendarClock} label="Bu Hafta Tahsilat" value={tl(recv?.due_this_week)} sub="Önümüzdeki 7 gün vadeli" tone="sky" testid="recv-week" />
        <StatCard icon={AlertTriangle} label="Vadesi Geçen" value={tl(recv?.overdue)} sub={`${recv?.overdue_count || 0} sevkiyat`} tone="red" testid="recv-overdue" />
      </div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="breed-breakdown">
        {(summary?.by_breed || []).map((b) => (
          <div key={b.breed} className="rounded-xl border border-border bg-card p-5" data-testid={`breed-card-${b.breed}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${BREED_TONE[b.breed] || ""}`}>{b.breed_label}</span>
              <Egg size={16} className="text-muted-foreground" />
            </div>
            <div className="font-head text-2xl font-bold tabular-nums" data-testid={`breed-eggs-${b.breed}`}>{fmt(b.total_eggs)}</div>
            <div className="text-xs text-muted-foreground">adet · %{fmtDec(b.avg_yield)} randıman · {fmt(b.total_chickens)} tavuk</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <h3 className="font-head font-semibold mb-4">Randıman Eğrisi (Son 30 Gün · Kümes Karşılaştırmalı)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trends?.yield_curve || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} unit="%" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {(trends?.coop_names || []).map((cn, i) => (
                <Line key={cn} type="monotone" dataKey={cn} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-head font-semibold mb-4">Boylama Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={trends?.sizing || []} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={90} label={(e) => e.name}>
                {(trends?.sizing || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
          <h3 className="font-head font-semibold mb-4">Fire Dağılımı (Son 30 Gün)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={trends?.defects || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} name="Adet" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-3 rounded-xl border border-border bg-card p-5">
          <h3 className="font-head font-semibold mb-4">Yem Maliyeti & FCR Trendi (Son 6 Ay)</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={costTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} unit="₺" />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="fcr" name="FCR" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="cost_per_egg" name="Yumurta Başı Maliyet (₺)" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
